import React from 'react';
import cn from '../utils/cn';
import KoobInputToggle from './KoobInputToggle';
import KoobTooltip from './KoobTooltip';

type KoobEnableableSectionProps = Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'className' | 'title'
> & {
  title: React.ReactNode;
  /** Shown in a tooltip on the title */
  titleTip?: React.ReactNode;
  description?: React.ReactNode;
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  disabled?: boolean;
  /** Replaces the built-in toggle (e.g. a segmented control) */
  control?: React.ReactNode;
  /** Overrides children visibility; defaults to `checked` */
  open?: boolean;
};

const KoobEnableableSection = React.forwardRef<
  HTMLDivElement,
  KoobEnableableSectionProps
>(
  (
    {
      title,
      titleTip,
      description,
      checked = false,
      onCheckedChange,
      disabled,
      control,
      open,
      children,
      ...props
    },
    ref,
  ) => {
    const isOpen = open ?? checked;

    return (
      <div
        ref={ref}
        className="w-full rounded-lg border border-solid border-gray-200 bg-gray-50/50 p-4"
        {...props}
      >
        <div
          className={cn(
            'flex items-center justify-between gap-4',
            isOpen && 'border-0 border-b border-solid border-gray-200 pb-4',
          )}
        >
          <div className="min-w-0">
            <KoobTooltip tip={titleTip} side="top" disabled={!titleTip}>
              <div className="flex items-center gap-1.5">
                <div className="font-medium text-gray-900">{title}</div>

                {titleTip && (
                  <i className="fa-regular fa-circle-info text-gray-400" />
                )}
              </div>
            </KoobTooltip>

            {description && (
              <div className="mt-1 text-sm text-gray-500">{description}</div>
            )}
          </div>

          <div className="shrink-0">
            {control ?? (
              <KoobInputToggle
                checked={checked}
                onCheckedChange={onCheckedChange}
                disabled={disabled}
              />
            )}
          </div>
        </div>

        {isOpen && <div className="pt-4 space-y-3">{children}</div>}
      </div>
    );
  },
);

KoobEnableableSection.displayName = 'KoobEnableableSection';

export default KoobEnableableSection;
